/**
 * PdfNode - メモに PDF を埋め込む Tiptap カスタムノード。
 *
 * src が無い間はアップロード欄、アップロード後はプレビューを表示する (pdf-node-view.tsx)。
 */

import { mergeAttributes, Node } from '@tiptap/core'
import type { NodeViewProps } from '@tiptap/react'
import { ReactNodeViewRenderer } from '@tiptap/react'

import { pendingFileIdAttribute } from '@/components/tiptap-extension/file-drop-paste-extension'

import { PdfNodeView } from './pdf-node-view'

/** アップロード API が返す論理URLの接頭辞。これ以外の src は受け付けない */
export const NOTE_PDF_URL_PREFIX = '/api/memos/pdfs/'

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    pdfEmbed: {
      /** カーソル位置に PDF のアップロード欄を挿入する */
      insertPdfEmbed: () => ReturnType
    }
  }
}

export const PdfNode = Node.create({
  name: 'pdfEmbed',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,

  addAttributes() {
    return {
      src: {
        default: null as string | null,
        parseHTML: (el) => {
          const src = el.getAttribute('data-src')
          return src?.startsWith(NOTE_PDF_URL_PREFIX) ? src : null
        },
        renderHTML: (attrs) => (attrs.src ? { 'data-src': String(attrs.src) } : {}),
      },
      name: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-name') ?? '',
        renderHTML: (attrs) => ({ 'data-name': String(attrs.name ?? '') }),
      },
      size: {
        default: null as number | null,
        parseHTML: (el) => {
          const n = Number(el.getAttribute('data-size'))
          return Number.isFinite(n) && n > 0 ? n : null
        },
        renderHTML: (attrs) => (attrs.size != null ? { 'data-size': String(attrs.size) } : {}),
      },
      height: {
        default: null as number | null,
        parseHTML: (el) => {
          const n = Number(el.getAttribute('data-height'))
          return Number.isFinite(n) && n > 0 ? n : null
        },
        renderHTML: (attrs) => (attrs.height != null ? { 'data-height': String(attrs.height) } : {}),
      },
      pendingFileId: pendingFileIdAttribute,
    }
  },

  parseHTML() {
    return [{ tag: 'div[data-type="pdf-embed"]' }]
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes({ 'data-type': 'pdf-embed' }, HTMLAttributes)]
  },

  // ファイル名でメモを検索できるよう content_text に載せる
  renderText({ node }) {
    return String(node.attrs.name ?? '')
  },

  addNodeView() {
    return ReactNodeViewRenderer(PdfNodeView as (props: NodeViewProps) => React.JSX.Element)
  },

  addCommands() {
    return {
      insertPdfEmbed:
        () =>
        ({ chain }) =>
          chain().insertContent({ type: this.name }).run(),
    }
  },
})
